import React from "react";
import { AiServiceUsageReport, AiServiceUsageReportItem, MetricsService } from "gen/clients";
import { ExportButton } from "components/ExportButton/ExportButton";
import { default as dayjs } from "dayjs";
import { formatDate } from "utils/dateUtils";

interface Props {
  clientId: string;
  from: Date;
  to: Date;
}

interface ExportedDataRow {
  username: string;
  requestType: string;
  characterCount: number;
  sourceLanguage: string;
  targetLanguages: string;
  dateTime: string;
  [key: string]: string | number;
}

const columnHeaders = {
  username: "User name",
  requestType: "Request Type",
  characterCount: "Character count",
  sourceLanguage: "Source Language",
  targetLanguages: "Target Languages",
  dateTime: "Date/Time"
};

const ExportAnalyticsButton: React.FC<Props> = ({ clientId, from, to }) => {
  const getExportData = React.useCallback(async (): Promise<ExportedDataRow[]> => {
    let allItems: AiServiceUsageReportItem[] = [];
    let nextPageToken: string | undefined = undefined;

    // Keep fetching until no more pages
    do {
      const response: AiServiceUsageReport = await MetricsService.getAiServiceUsageReport({
        clientId,
        fromDate: from.toISOString(),
        toDate: to.toISOString(),
        pageToken: nextPageToken,
        pageSize: 100
      });

      allItems = [...allItems, ...response.items];
      nextPageToken = response.nextPageToken;
    } while (nextPageToken);

    return allItems.map(row => ({
      username: row.username,
      requestType: row.requestType,
      characterCount: row.characterCount,
      sourceLanguage: row.sourceLanguage,
      targetLanguages: row.targetLanguages,
      dateTime: formatDate(row.dateTime)
    }));
  }, [clientId, from, to]);

  const fileName = `ai-service-usage-${dayjs(from).format("MM-DD-YYYY")}-${dayjs(to).format("MM-DD-YYYY")}.csv`;

  return (
    <ExportButton<ExportedDataRow>
      filename={fileName}
      columnHeaders={columnHeaders}
      getExportData={getExportData}
      exportingMessage="Exporting records. This may take a moment..."
    />
  );
};

export { ExportAnalyticsButton };
